import {
  DATE_RANGE_PRESETS,
  findMatchingPreset,
  formatDateRange,
} from '@components/DateRangeSelector/dateRangePresets';

import type { FilterDescription, FilterFieldDef, FiltersState, FilterValue } from './types';

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** Parse an ISO `yyyy-MM-dd` string as local midnight (never UTC-shifted). */
export const isoToLocalDate = (iso: string): Date => new Date(`${iso}T00:00:00`);

/** Format a Date as local ISO `yyyy-MM-dd`. */
export const dateToIso = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

/** An empty value means the filter is effectively unapplied (its chip disappears). */
export const isEmptyFilterValue = (value: FilterValue): boolean => {
  switch (value.kind) {
    case 'multiSelect':
      return value.values.length === 0;
    case 'boolean':
      return false;
    case 'dateRange':
      return value.start === null && value.end === null;
  }
};

// Deliberately dumb count noun: "statuses", "labels", "assignees".
const pluralize = (label: string): string => {
  const lower = label.toLowerCase();
  return lower.endsWith('s') ? `${lower}es` : `${lower}s`;
};

/**
 * Human-readable summary of one applied filter for chips and aria-labels.
 * Mismatched def/value kinds fall back to a generic description rather than
 * throwing (stale URL state should never crash the chips row).
 */
export const describeFilter = (def: FilterFieldDef, value: FilterValue): FilterDescription => {
  if (def.type === 'multiSelect' && value.kind === 'multiSelect') {
    const labelByValue = new Map(def.options.map(option => [option.value, option.label]));
    const labels = value.values.map(raw => labelByValue.get(raw) ?? raw);
    if (labels.length === 1) {
      return { field: def.label, op: 'is', value: labels[0] };
    }
    return {
      field: def.label,
      op: 'is any of',
      value: labels.length <= 2 ? labels.join(', ') : `${labels.length} ${pluralize(def.label)}`,
    };
  }

  if (def.type === 'boolean' && value.kind === 'boolean') {
    return {
      field: def.label,
      op: 'is',
      value: value.value ? (def.trueLabel ?? 'Yes') : (def.falseLabel ?? 'No'),
    };
  }

  if (def.type === 'dateRange' && value.kind === 'dateRange') {
    const range = {
      start: value.start ? isoToLocalDate(value.start) : null,
      end: value.end ? isoToLocalDate(value.end) : null,
    };
    const preset = value.preset
      ? (DATE_RANGE_PRESETS.find(candidate => candidate.value === value.preset) ?? null)
      : findMatchingPreset(range);
    return { field: def.label, op: 'is', value: preset?.label ?? formatDateRange(range) };
  }

  if (def.type === 'custom') {
    return { field: def.label, ...def.describeValue(value) };
  }

  return { field: def.label, op: 'is', value: '—' };
};

/**
 * Serialize applied filters into a single URL-param payload. Empty values are
 * dropped; returns null when nothing survives (callers delete the param).
 */
export const serializeFiltersToParam = (state: FiltersState): string | null => {
  const entries = Object.entries(state).filter(([, value]) => !isEmptyFilterValue(value));
  if (entries.length === 0) return null;
  return JSON.stringify(Object.fromEntries(entries));
};

const validateValue = (def: FilterFieldDef, raw: unknown): FilterValue | null => {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Record<string, unknown>;

  switch (def.type) {
    case 'multiSelect':
      return candidate.kind === 'multiSelect' &&
        Array.isArray(candidate.values) &&
        candidate.values.every(entry => typeof entry === 'string')
        ? { kind: 'multiSelect', values: candidate.values }
        : null;
    case 'boolean':
      return candidate.kind === 'boolean' && typeof candidate.value === 'boolean'
        ? { kind: 'boolean', value: candidate.value }
        : null;
    case 'dateRange': {
      // Regex-shaped but calendar-invalid strings ('2026-02-30', '2026-13-01')
      // must not survive: they'd render "Invalid Date" chips and break the
      // DatePicker. The round-trip check also rejects normalized dates.
      const isValidBound = (bound: unknown): bound is string | null => {
        if (bound === null) return true;
        if (typeof bound !== 'string' || !ISO_DATE.test(bound)) return false;
        const date = isoToLocalDate(bound);
        return !Number.isNaN(date.getTime()) && dateToIso(date) === bound;
      };
      if (candidate.kind !== 'dateRange') return null;
      const { start, end } = candidate;
      if (!isValidBound(start) || !isValidBound(end)) return null;
      // ISO strings compare chronologically; inverted ranges are dropped too.
      if (start !== null && end !== null && start > end) return null;
      const preset = candidate.preset;
      if (preset !== undefined) {
        if (typeof preset !== 'string') return null;
        const definition = DATE_RANGE_PRESETS.find(entry => entry.value === preset);
        const range = definition?.getRange();
        if (!range?.start || !range.end) return null;
        return {
          kind: 'dateRange',
          preset,
          start: dateToIso(range.start),
          end: dateToIso(range.end),
        };
      }
      return { kind: 'dateRange', start, end };
    }
    case 'custom':
      return null;
  }
};

/**
 * Parse a URL-param payload back into FiltersState. Untrusted by definition:
 * malformed JSON yields `{}`, and entries with unknown field ids or wrong
 * value shapes are silently dropped.
 */
export const parseFiltersFromParam = (
  raw: string | null,
  fields: FilterFieldDef[],
): FiltersState => {
  if (!raw) return {};

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return {};
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

  const defsById = new Map(fields.map(field => [field.id, field]));
  const next: FiltersState = {};
  for (const [id, rawValue] of Object.entries(parsed as Record<string, unknown>)) {
    const def = defsById.get(id);
    if (!def) continue;
    const value = validateValue(def, rawValue);
    if (value && !isEmptyFilterValue(value)) next[id] = value;
  }
  return next;
};
